import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ProfessionalCreationDto,
  ProfessionalRestoreRequestDto,
  ProfessionalSetPasswordDto,
  ProfessionalSetPasswordResDto,
  ProfessionalUpdateDto,
  SupportedLanguageIso,
  UserChangeEmailDto,
  UserChangePhoneDto,
  UserRequestChangePhoneDto,
  UserVerifyCodeDto,
} from '@docbay/schemas';
import { ProfessionalsActions } from 'applicaiton/constants/actions';
import { ProfessionalsAPI } from 'integration/api/professionals';
import { fetchProfessionalsProps } from 'common/types/professionals';
import { AxiosError } from 'axios';
import { setIsVerificationCodeExpired } from './ProfessionalsSlice';
import {
  getToken,
  setProfilesInSessionStorage,
  setUsersAuthSessionStorage,
} from 'applicaiton/sessionStorage/auth';
import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';
import { UsersAPI } from 'integration/api/users';
import { ProfessionalsSubscribersParams } from './models';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetches a list of professionals based on the provided parameters.
 *
 * @param params - The parameters for fetching professionals.
 * @returns The fetched professionals data or an error message if rejected.
 */
export const fetchProfessionals = createAsyncThunk(
  ProfessionalsActions.fetchProfessionals,
  async (params: fetchProfessionalsProps, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.fetchProfessionals(params);

      await delay(1000);

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches professionals for autosuggest based on the provided parameters.
 *
 * @param params - The parameters for fetching professionals.
 * @returns The fetched professionals data or an error message if rejected.
 */
export const fetchProfessionalsForAutosuggest = createAsyncThunk(
  ProfessionalsActions.fetchProfessionalsForAutosuggest,
  async (params: fetchProfessionalsProps, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.fetchProfessionals(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches a professional by ID.
 *
 * @param id - The ID of the professional to fetch.
 * @returns The fetched professional data or an error message if rejected.
 */
export const fetchProfessionalById = createAsyncThunk(
  ProfessionalsActions.fetchProfessionalById,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.fetchProfessionalById(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a professional by ID.
 *
 * @param id - The ID of the professional to delete.
 * @returns The deleted professional data or an error message if rejected.
 */
export const deleteProfessionals = createAsyncThunk(
  ProfessionalsActions.deleteProfessional,
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.removeProfessional(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new professional.
 *
 * @param professional - The professional creation DTO.
 * @returns The created professional data or an error message if rejected.
 */
export const createProfessional = createAsyncThunk(
  ProfessionalsActions.createProfessional,
  async (professional: ProfessionalCreationDto, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.createProfessional(professional);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates a professional by ID.
 *
 * @param params - The parameters for updating the professional, including the ID and the updated professional DTO.
 * @returns The updated professional data or an error message if rejected.
 */
export const updateProfessional = createAsyncThunk(
  ProfessionalsActions.updateProfessional,
  async (
    params: { id: string; professional: Partial<ProfessionalUpdateDto> },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ProfessionalsAPI.updateProfessional(
        params.id,
        params.professional,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Resends authorization data for a professional by ID.
 *
 * @param id - The ID of the professional to resend authorization data for.
 * @returns The resended authorization data or an error message if rejected.
 */
export const resendProfessionalAuthorizationData = createAsyncThunk(
  ProfessionalsActions.resendAuthorizationData,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.resendAuthorizationData(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Checks the verification code for a professional.
 *
 * @param params - The parameters for checking the verification code, including the email and the code.
 * @returns The result of checking the verification code or an error message if rejected.
 */
export const checkProfessionalVerificationCode = createAsyncThunk(
  ProfessionalsActions.checkProfessionalVerificationCode,
  async (
    params: { email: string; code: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await ProfessionalsAPI.checkProfessionalVerificationCode(
        params.email,
        params.code,
      );
      dispatch(setIsVerificationCodeExpired(false));
    } catch (e) {
      dispatch(setIsVerificationCodeExpired(true));

      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a professional password.
 *
 * @param params - The parameters for creating the professional password, including the email and the code.
 * @returns The result of creating the professional password or an error message if rejected.
 */
export const createProfessionalPassword = createAsyncThunk(
  ProfessionalsActions.createProfessionalPassword,
  async (params: ProfessionalSetPasswordDto, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.createProfessionalPassword(
        params,
      );
      const professionalProfile: ProfessionalSetPasswordResDto = data;
      const { token, expiresAt, refreshToken, user } = professionalProfile;

      setUsersAuthSessionStorage({ token, expiresAt, refreshToken, user });
      setProfilesInSessionStorage([
        {
          id: user?.id!,
          email: user?.email!,
          firstName: user?.firstName!,
          lastName: user?.lastName!,
          photo: user?.photo!,
          token,
          refreshToken,
        },
      ]);
      return professionalProfile.professional;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates the email of a professional by ID.
 *
 * @param params - The parameters for updating the professional email, including the ID and the new email.
 * @returns The result of updating the professional email or an error message if rejected.
 */
export const updateProfessionalEmail = createAsyncThunk(
  ProfessionalsActions.updateProfessionalEmail,
  async (params: { id: string; email: string }, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.updateProfessionalEmail(
        params.id,
        params.email,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the spoken languages of professionals.
 *
 * @returns The result of fetching the spoken languages or an error message if rejected.
 */
export const fetchProfessionalsSpokenLanguages = createAsyncThunk(
  ProfessionalsActions.updateProfessionalEmail,
  async (_, { rejectWithValue }) => {
    try {
      const { data } =
        await ProfessionalsAPI.fetchProfessionalsSpokenLanguages();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new clinic owner professional.
 *
 * @param profile - The professional creation DTO for the clinic owner.
 * @returns The result of creating the clinic owner professional or an error message if rejected.
 */
export const createClinicOwnerProfessional = createAsyncThunk(
  `professionals/${ProfessionalsActions.createClinicOwnerProfessional}`,
  async (profile: ProfessionalCreationDto, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.createClinicOwnerProfessional(
        profile,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies the old email of a professional.
 *
 * @param params - The parameters for verifying the old email, including the email.
 * @returns The result of verifying the old email or an error message if rejected.
 */
export const professionalVerifyOldEmail = createAsyncThunk(
  ProfessionalsActions.professionalVerifyOldEmail,
  async (params: { email: string }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const language = getLanguageFromLocalStorage();
      const { data } = await UsersAPI.userVerifyOldEmail(
        { ...params, language },
        token,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies the code for a professional.
 *
 * @param params - The parameters for verifying the code, including the email and the code.
 * @returns The result of verifying the code or an error message if rejected.
 */
export const professionalVerifyCode = createAsyncThunk(
  ProfessionalsActions.professionalVerifyCode,
  async (params: UserVerifyCodeDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersVerifyCode(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sets the new email for a professional.
 *
 * @param params - The parameters for setting the new email, including the email and the code.
 * @returns The result of setting the new email or an error message if rejected.
 */
export const professionalSetNewEmail = createAsyncThunk(
  ProfessionalsActions.professionalSetNewEmail,
  async (params: UserChangeEmailDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data } = await UsersAPI.userSetNewEmail(params, token);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Restores a clinic owner professional.
 *
 * @param params - The parameters for restoring the clinic owner professional, including the email and the code.
 * @returns The result of restoring the clinic owner professional or an error message if rejected.
 */
export const restoreClinicOwnerProfessional = createAsyncThunk(
  ProfessionalsActions.restoreClinicOwnerProfessional,
  async (params: ProfessionalRestoreRequestDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data } = await ProfessionalsAPI.restoreClinicOwnerProfessional(
        params,
        token,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies the phone of a professional.
 *
 * @param params - The parameters for verifying the phone, including the phone number and language.
 * @returns The result of verifying the phone or an error message if rejected.
 */
export const professionalVerifyPhone = createAsyncThunk(
  ProfessionalsActions.professionalVerifyPhone,
  async (params: UserRequestChangePhoneDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const language = getLanguageFromLocalStorage();

      const { data } = await UsersAPI.userPhoneVerificationCode(
        { ...params, language: language as SupportedLanguageIso },
        token,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sets the new phone for a professional.
 *
 * @param params - The parameters for setting the new phone, including the phone number and code.
 * @returns The result of setting the new phone or an error message if rejected.
 */
export const professionalSetNewPhone = createAsyncThunk(
  ProfessionalsActions.professionalSetNewPhone,
  async (params: UserChangePhoneDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data } = await UsersAPI.userSetNewPhone(params, token);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the subscribers of a professional.
 *
 * @param params - The parameters for fetching the subscribers, including the id and other optional params.
 * @returns The result of fetching the subscribers or an error message if rejected.
 */
export const fetchProfessionalsSubscribers = createAsyncThunk(
  ProfessionalsActions.fetchProfessionalsSubscribers,
  async (
    { id, params }: { id: string; params: ProfessionalsSubscribersParams },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ProfessionalsAPI.fetchPurchasedSubscription(
        id,
        params,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the subscriptions of a professional.
 *
 * @param params - The parameters for fetching the subscriptions, including the clinic ids.
 * @returns The result of fetching the subscriptions or an error message if rejected.
 */
export const fetchSubscriptions = createAsyncThunk(
  ProfessionalsActions.fetchSubscriptions,
  async (params: { clinicIds?: string[] }, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.fetchSubscriptions(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the subscription statistic of a professional.
 *
 * @param params - The parameters for fetching the subscription statistic, including the package ids.
 * @returns The result of fetching the subscription statistic or an error message if rejected.
 */
export const fetchSubscriptionStatistic = createAsyncThunk(
  ProfessionalsActions.fetchSubscriptionStatistic,
  async (params: { packageIds?: string[] }, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.fetchSubscriptionStatistic(
        params,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the subscribed patients of a professional.
 *
 * @param search - The search term for finding subscribed patients.
 * @returns The result of fetching the subscribed patients or an error message if rejected.
 */
export const fetchSubscribedPatients = createAsyncThunk(
  ProfessionalsActions.fetchSubscriptionStatistic,
  async (search: string, { rejectWithValue }) => {
    try {
      const { data } = await ProfessionalsAPI.fetchSubscribedPatients(search);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
